import { IonBadge, IonContent, IonHeader, IonChip, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonList, IonPage, IonTitle, IonToolbar, IonPopover, IonButton, IonText, useIonModal, IonSpinner, IonSkeletonText, IonThumbnail, useIonAlert } from '@ionic/react';
import { IonButtons, IonBackButton, IonItem, IonLabel, IonProgressBar } from '@ionic/react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { RouteComponentProps } from 'react-router';
import DeviceEventItem from '../components/DeviceEventItem';
import { useDeviceEventsQuery } from '../query/deviceEventsQuery';
import { useMemo } from 'react';
import { useDeviceQuery, useUpdateDeviceMutation } from '../query/deviceQuery';
import { chevronExpandOutline, copyOutline, createOutline, ellipsisVertical, lockOpenOutline, peopleOutline, refreshOutline, shieldHalf, trashOutline } from "ionicons/icons";
import DeviceTransitPolicyTile from '../components/DeviceTransitPolicyTile';
import DeviceTransitPolicyAddButton from '../components/DeviceTransitPolicyAddButton';
import { useDeviceTransitPoliciesQuery } from '../query/deviceTransitPoliciesQuery';
import styles from './Device.module.css';
import OCGatewayClient from '../client/OCGatewayClient';
import useUX from '../utils/ux';
import DeviceTimeZoneModal from '../modals/DeviceTimeZoneModal';
import DeviceFirmwareChannelModal from '../modals/DeviceFirmwareChannelModal';
import DeviceScreenTemplateModal from '../modals/DeviceScreenTemplateModal';
import DeviceLocaleModal from '../modals/DeviceLocaleModal';
import { screenTemplateById } from '../utils/screenTemplates';
import { deviceLanguageOf } from '../utils/deviceLocale';
import { useJiraDeviceTicketsQuery } from '../query/jiraDeviceTicketsQuery';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import DeviceUsersModal from '../modals/DeviceUsersModal';
import { useDeveloperModeQuery } from '../query/developerModeQuery';
import { useDevicePaymentPlanQuery } from '../query/devicePaymentPlanQuery';
import DevicePaymentPlanModal from '../modals/DevicePaymentPlanModal';
import DeviceConnectivityChip from '../components/DeviceConnectivityChip';
import SectionHeader from '../components/SectionHeader';
import sectionStyles from '../components/SectionHeader.module.css';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useDeleteDeviceUserAccessMutation } from '../query/deviceUsersQuery';

interface DevicePageProps
    extends RouteComponentProps<{
        deviceId: string;
    }> { }

const DEVICE_EVENTS_PAGE_SIZE = 10;

const Device: React.FC<DevicePageProps> = ({ match }) => {
    const { deviceId } = match.params;
    const deviceQuery = useDeviceQuery(deviceId);
    const deviceMutation = useUpdateDeviceMutation(deviceId);
    const deviceEventsQuery = useDeviceEventsQuery(deviceId, undefined, DEVICE_EVENTS_PAGE_SIZE);
    const devicePaymentPlanQuery = useDevicePaymentPlanQuery(deviceId);
    const deviceTransitPoliciesQuery = useDeviceTransitPoliciesQuery(deviceId);
    const { presentResult } = useUX();
    const developerModeQuery = useDeveloperModeQuery();
    const developerMode = !!developerModeQuery.data;
    const { t } = useTranslation('pages');
    const { t: tCommon } = useTranslation('common');
    const history = useHistory();
    const [policyRowRef] = useAutoAnimate<HTMLDivElement>();

    // Info for Admins
    const currentUserQuery = useCurrentUserQuery();
    const deleteDeviceUserAccessMutation = useDeleteDeviceUserAccessMutation(deviceId);
    const isAdmin = currentUserQuery.data?.userLevel === 'ADMIN';
    const deviceJiraTicketsQuery = useJiraDeviceTicketsQuery(isAdmin ? deviceId : undefined);

    const events = useMemo(() => {
        return deviceEventsQuery.data?.pages.reduce((acc, page) => acc.concat(page), []) ?? [];
    }, [deviceEventsQuery.data]);

    const activeTransitPolicyId = deviceQuery.data?.deviceTransitPolicyId;

    // Active policy first, then most recently activated; ties keep the server order.
    const sortedTransitPolicies = useMemo(() => {
        const policies = deviceTransitPoliciesQuery.data;
        if (!policies) {
            return policies;
        }

        const rankOf = (policy: (typeof policies)[number]) =>
            policy.deviceTransitPolicyId === activeTransitPolicyId
                ? Number.POSITIVE_INFINITY
                : policy.activatedAt ?? 0;

        return [...policies].sort((a, b) => rankOf(b) - rankOf(a));
    }, [deviceTransitPoliciesQuery.data, activeTransitPolicyId]);

    const unlock = async () => {
        presentResult(() => OCGatewayClient.request('runDeviceCommand', { deviceId, command: 'unlock' }), t('device.remote_unlock_requested'));
    };

    const reboot = async () => {
        presentResult(() => OCGatewayClient.request('runDeviceCommand', { deviceId, command: 'reboot' }), t('device.reboot_requested'));
    };

    const copyDeviceId = () => {
        presentResult(async () => {
            await navigator.clipboard.writeText(deviceId);
        }, t('device.device_id_copied'));
    };

    const [presentTimeZoneModal, dismissTimeZoneModal] = useIonModal(DeviceTimeZoneModal, {
        dismiss: (data: string, role: string) => dismissTimeZoneModal(data, role),
        deviceId
    });

    const [presentScreenTemplateModal, dismissScreenTemplateModal] = useIonModal(DeviceScreenTemplateModal, {
        dismiss: (data: string, role: string) => dismissScreenTemplateModal(data, role),
        deviceId
    });

    const [presentLocaleModal, dismissLocaleModal] = useIonModal(DeviceLocaleModal, {
        dismiss: (data: string, role: string) => dismissLocaleModal(data, role),
        deviceId
    });

    // Display values: a template/locale the app can name, else the raw id/tag, else Default.
    const screenTemplate = deviceQuery.data?.screenTemplate;
    const knownScreenTemplate = screenTemplateById(screenTemplate);
    const screenTemplateLabel = knownScreenTemplate
        ? tCommon(knownScreenTemplate.labelKey)
        : (screenTemplate || t('device.display_default'));
    const deviceLocale = deviceQuery.data?.locale;
    const deviceLanguage = deviceLanguageOf(deviceLocale);
    const deviceLanguageLabel = deviceLocale
        ? (deviceLanguage ? tCommon(`language_names.${deviceLanguage}`) : deviceLocale)
        : t('device.display_default');

    const [presentUsersModal, dismissUsersModal] = useIonModal(DeviceUsersModal, {
        dismiss: (data: string, role: string) => dismissUsersModal(data, role),
        deviceId
    });

    const [presentFirmwareChannelModal, dismissFirmwareChannelModal] = useIonModal(DeviceFirmwareChannelModal, {
        dismiss: (data: string, role: string) => dismissFirmwareChannelModal(data, role),
        deviceId
    });

    const [presentPaymentPlanModal, dismissPaymentPlanModal] = useIonModal(DevicePaymentPlanModal, {
        dismiss: (data: string, role: string) => dismissPaymentPlanModal(data, role),
        deviceId
    });

    const [presentAlert] = useIonAlert();

    const removeDevice = () => {
        presentAlert({
            header: t('device.remove_device_alert_header'),
            subHeader: deviceQuery.data?.description || deviceId,
            message: t('device.remove_device_alert_message'),
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: tCommon('buttons.delete'),
                    role: 'destructive',
                    handler: async () => {
                        const currentUserId = currentUserQuery.data?.id;
                        if (!currentUserId) return;

                        await presentResult(
                            async () => {
                                await deleteDeviceUserAccessMutation.mutateAsync(currentUserId);
                                history.push('/devices');
                            },
                            t('device.device_removed')
                        );
                    }
                }
            ]
        });
    };

    const renameDevice = async () => {
        presentAlert({
            header: t('device.rename_device'),
            inputs: [
                {
                    name: 'name',
                    type: 'text',
                    value: deviceQuery.data?.description,
                    placeholder: t('device.device_name_placeholder'),
                    attributes: {
                        maxlength: 64,
                    },
                }
            ],
            buttons: [
                tCommon('buttons.cancel'),
                {
                    text: tCommon('buttons.save'),
                    handler: (data) => {
                        if (data.name === deviceQuery.data?.description) return;
                        presentResult(() => deviceMutation.mutateAsync({ description: data.name || null }), t('device.device_renamed'));
                    }
                }
            ]
        });
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/devices" />
                    </IonButtons>
                    <IonTitle>{t('device.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton id="device-users-modal" onClick={() => presentUsersModal({ presentingElement: document.getElementsByTagName('ion-router-outlet')[0], cssClass: 'oc-dialog-m' })}>
                            <IonIcon slot="icon-only" icon={peopleOutline} aria-label={t('device.device_users')} />
                        </IonButton>
                    </IonButtons>
                    <IonButtons slot="primary">
                        <IonButton id="device-more" aria-label={t('device.device_control_menu')} aria-haspopup="menu" fill="clear">
                            <IonIcon slot="icon-only" icon={ellipsisVertical} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                {(deviceQuery.isLoading || (deviceEventsQuery.isLoading && !deviceEventsQuery.isFetchingNextPage)) && <IonProgressBar type="indeterminate" />}
            </IonHeader>
            <IonContent color="light" fullscreen>
                <div className="oc-page-container">
                <div className={`${styles.hero} ${deviceQuery.data && !deviceQuery.data.connectivity?.connected ? styles.offline : ''}`}>
                    <img className={styles.heroPhoto} src="/assets/device/onlycat-device.png" alt="" />
                    <h1 className={styles.heroName}>
                        {deviceQuery.isLoading
                            ? <IonSkeletonText animated={true} style={{ width: '140px' }} />
                            : (deviceQuery.data?.description || deviceId)}
                    </h1>
                    {deviceQuery.data?.description && <div className={styles.heroId}>{deviceId}</div>}
                    <div className={styles.heroChip}>
                        <DeviceConnectivityChip
                            deviceId={deviceId}
                            connectivity={deviceQuery.data?.connectivity}
                        />
                    </div>
                </div>
                <SectionHeader>{t('device.door_policy')}</SectionHeader>
                {sortedTransitPolicies && <div ref={policyRowRef} className={styles.policyRow}>
                    {sortedTransitPolicies.map((policy) => <DeviceTransitPolicyTile
                        key={policy.deviceTransitPolicyId}
                        deviceId={deviceId}
                        deviceTransitPolicyId={policy.deviceTransitPolicyId!}
                        active={policy.deviceTransitPolicyId === activeTransitPolicyId}
                    />)}
                    <DeviceTransitPolicyAddButton deviceId={deviceId} />
                </div>}
                <SectionHeader>{t('device.device_information')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    <IonItem button detail={true} detailIcon={copyOutline} onClick={copyDeviceId}>
                        <IonLabel>{t('device.device_id')}</IonLabel>
                        <IonText slot="end" color="dark">{deviceId}</IonText>
                    </IonItem>
                    <IonItem onClick={() => presentTimeZoneModal({ presentingElement: document.getElementsByTagName('ion-router-outlet')[0], cssClass: 'oc-dialog-s' })} button detail detailIcon={chevronExpandOutline}>
                        <IonLabel>{t('device.time_zone')}</IonLabel>
                        <IonText slot="end" color="medium">{deviceQuery.data?.timeZone || t('device.not_set')}</IonText>
                    </IonItem>
                    <IonItem onClick={() => presentPaymentPlanModal({ presentingElement: document.getElementsByTagName('ion-router-outlet')[0], cssClass: 'oc-dialog-m' })} button detail detailIcon={chevronExpandOutline}>
                        <IonLabel>{t('device.payment_plan_title')}</IonLabel>
                        <IonText
                            slot="end"
                            color={devicePaymentPlanQuery.data?.state === 'required' ? 'danger' : 'medium'}
                            style={{ textTransform: 'capitalize' }}
                        >
                            {devicePaymentPlanQuery.data?.state === 'required' && (
                                <IonBadge
                                    className="payment-dot"
                                    color="danger"
                                    style={{ verticalAlign: 'middle', marginInlineEnd: '6px' }}
                                />
                            )}
                            {devicePaymentPlanQuery.data?.state || t('device.coming_soon')}
                        </IonText>
                    </IonItem>
                    {developerMode && (
                        <IonItem onClick={() => presentFirmwareChannelModal({ presentingElement: document.getElementsByTagName('ion-router-outlet')[0], cssClass: 'oc-dialog-s' })} button detail detailIcon={chevronExpandOutline}>
                            <IonLabel>{t('device.firmware_channel')}</IonLabel>
                            <IonText slot="end" color="medium" style={{ textTransform: 'capitalize' }}>{deviceQuery.data?.firmwareChannel || t('device.standard')}</IonText>
                        </IonItem>
                    )}
                </IonList>
                <SectionHeader>{t('device.display')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    <IonItem onClick={() => presentScreenTemplateModal({ presentingElement: document.getElementsByTagName('ion-router-outlet')[0], cssClass: 'oc-dialog-s' })} button detail detailIcon={chevronExpandOutline}>
                        <IonLabel>{t('device.screen')}</IonLabel>
                        <IonText slot="end" color="medium">{screenTemplateLabel}</IonText>
                    </IonItem>
                    <IonItem onClick={() => presentLocaleModal({ presentingElement: document.getElementsByTagName('ion-router-outlet')[0], cssClass: 'oc-dialog-s' })} button detail detailIcon={chevronExpandOutline}>
                        <IonLabel>{t('device.language')}</IonLabel>
                        <IonText slot="end" color="medium">{deviceLanguageLabel}</IonText>
                    </IonItem>
                </IonList>
                <p className={styles.sectionNote}>{t('device.screen_hint')}</p>
                {isAdmin && <>
                    <SectionHeader
                        end={deviceJiraTicketsQuery.isLoading ? <IonSpinner name="dots" /> : <IonIcon icon={shieldHalf} size="small" />}
                    >
                        {t('device.relevant_tickets')}
                    </SectionHeader>
                    <IonList inset={true} className={`${sectionStyles.insetList} ${styles.jiraTickets}`}>
                    {deviceJiraTicketsQuery.isError && <IonItem>
                        <IonLabel>{t('device.error_loading_tickets')}</IonLabel>
                    </IonItem>}
                    {deviceJiraTicketsQuery.isSuccess &&
                        deviceJiraTicketsQuery.data?.map((ticket: any) =>
                            <IonItem key={ticket.key} onClick={e => window.open(ticket.url, '_blank')} button>
                                <IonThumbnail aria-hidden slot="start">
                                    <img alt="" src={ticket.iconUrl} />
                                </IonThumbnail>
                                <IonLabel>
                                    <IonText>{ticket.summary}</IonText>
                                    <p>{ticket.key}</p>
                                </IonLabel>
                                <IonText slot="end" color="medium">{ticket.status}</IonText>
                            </IonItem>)
                    }
                </IonList></>}
                <SectionHeader>{t('device.recent_events')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    {events?.map((event: any) => <DeviceEventItem key={event.eventId} deviceId={event.deviceId} eventId={event.eventId} accessToken={event.accessToken} />)}
                </IonList>
                <IonInfiniteScroll
                    threshold="120px"
                    disabled={!deviceEventsQuery.hasNextPage}
                    onIonInfinite={async (event) => {
                        try {
                            if (!deviceEventsQuery.isFetchingNextPage) {
                                await deviceEventsQuery.fetchNextPage();
                            }
                        } finally {
                            await event.target.complete();
                        }
                    }}
                >
                    <IonInfiniteScrollContent />
                </IonInfiniteScroll>
                </div>
            </IonContent>
            <IonPopover trigger="device-more" dismissOnSelect>
                <IonContent>
                    <IonList>
                        <IonItem button={true} detail={false} onClick={() => renameDevice()}>
                            <IonIcon slot="start" icon={createOutline} />
                            <IonLabel>{t('device.rename_device')}</IonLabel>
                        </IonItem>
                        <IonItem button={true} detail={false} onClick={unlock}>
                            <IonIcon slot="start" icon={lockOpenOutline} />
                            <IonLabel>{t('device.remote_unlock')}</IonLabel>
                        </IonItem>
                        <IonItem button={true} detail={false} onClick={reboot}>
                            <IonIcon slot="start" icon={refreshOutline} />
                            <IonLabel>{t('device.reboot')}</IonLabel>
                        </IonItem>
                        <IonItem
                            button={true}
                            detail={false}
                            onClick={removeDevice}
                            disabled={!currentUserQuery.data?.id || deleteDeviceUserAccessMutation.isPending}
                        >
                            <IonIcon slot="start" icon={trashOutline} color="danger" />
                            <IonLabel color="danger">{t('device.remove_device')}</IonLabel>
                        </IonItem>
                    </IonList>
                </IonContent>
            </IonPopover>
        </IonPage >
    );
};

export default Device;
